import {useEffect, useState} from "@wordpress/element";
import {
    Box, Stack, Typography,
    Grid, Card, CardMedia, CardActionArea,
    CircularProgress, Container, Button, useMediaQuery, useTheme
} from '@mui/material';
import {SpacesControls} from '../../common/SpacesControls'
import {
    applyState,
    formatAvailType,
    formatSuite,
    isEmpty,
    maybeImgLinkToSpace,
    maybeLinkToSpace,
    toParam
} from "../../common/util";
import {addQueryArgs} from "@wordpress/url";
import apiFetch from "@wordpress/api-fetch";

export function SpaceCard({space, mini = false, dense = false, height = '100%'}) {
    const title = `${space['PropertyName']}${formatSuite(space['Suite'])}`;
    let maybeGoToSpace = null;
    if (space['Space_ID'] !== null && space['Space_ID'] !== '') {
        maybeGoToSpace = (_) => {
            if (space['Space_ID']) {
                const spaceDetailURL = `${window.location.origin}/space/?id=${space['Space_ID']}`;
                window.location.assign(spaceDetailURL);
            }
        };
    }
    return (
        <Card sx={{height: height, opacity: (maybeGoToSpace != null) ? 1 : 0.38}}>
            <CardActionArea sx={{height: '100%'}} disabled={maybeGoToSpace == null}
                            onClick={maybeGoToSpace}>
                <CardMedia sx={{flexShrink: 2, flexGrow: 2, height: '55%'}}>
                    {maybeImgLinkToSpace(space, title)}
                </CardMedia>
                <Container disableGutters fixed sx={{
                    height: '45%',
                    minWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignContent: 'start',
                    justifyContent: 'space-between',
                    padding: '5px',
                }}>
                    {maybeLinkToSpace(space, title, mini, dense)}
                    <Container disableGutters sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                    }}>
                        <Typography color="text.secondary" component='span'
                                    sx={{fontSize: 'clamp(0.8rem, -0.4rem + 4vw, 1rem)'}}>
                            +/- {Number(space['AvailableSqft']).toLocaleString()} RSF
                        </Typography>
                        <Typography color="text.secondary" component='span'
                                    sx={{fontSize: 'clamp(0.8rem, -0.4rem + 4vw, 1rem)'}}>
                            {formatAvailType(space['AvailType'])}{space['LeaseType']}
                        </Typography>
                    </Container>
                </Container>
            </CardActionArea>
        </Card>
    );
}

function SpacesGrid({attributes}) {
    const [city, setCity] = useState(toParam(attributes.city));
    const [propertyType, setPropertyType] = useState(toParam(attributes.propertyType));
    const [subType, setSubType] = useState(toParam(attributes.subType));
    const [availType, setAvailType] = useState(toParam(attributes.availType));
    const [minSqft, setMinSqft] = useState(toParam(attributes.minSqft));
    const [maxSqft, setMaxSqft] = useState(toParam(attributes.maxSqft));
    const [minRent, setMinRent] = useState(toParam(attributes.minRent));
    const [maxRent, setMaxRent] = useState(toParam(attributes.maxRent));
    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingAll, setLoadingAll] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [page, setPage] = useState(0);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isXL = useMediaQuery(theme.breakpoints.up('xl'));
    const isMD = useMediaQuery(theme.breakpoints.up('md'));
    let pageSize;
    if (isXL) {
        pageSize = 12;
    } else if (isMD) {
        pageSize = 6;
    } else {
        pageSize = 4;
    }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        console.log('SpacesGrid attributes changed', attributes, params);
        applyState(setCity, params, 'city', attributes.city);
        applyState(setPropertyType, params, 'propertyType', attributes.propertyType);
        applyState(setSubType, params, 'subType', attributes.subType);
        applyState(setAvailType, params, 'availType', attributes.availType);
        applyState(setMinSqft, params, 'minSqft', attributes.minSqft);
        applyState(setMaxSqft, params, 'maxSqft', attributes.maxSqft);
        applyState(setMinSqft, params, 'minRent', attributes.minRent);
        applyState(setMaxSqft, params, 'maxRent', attributes.maxRent);
    }, [attributes.city, attributes.propertyType, attributes.subType, attributes.availType,
        attributes.minSqft, attributes.maxSqft, attributes.minRent, attributes.maxRent]);

    const fetch = () => {
        let urlPath = addQueryArgs('/ebo-spaces/v1/spaces', {
            city: isEmpty(city) ? null : city,
            type: isEmpty(propertyType) ? null : propertyType,
            subType: isEmpty(subType) ? null : subType,
            availType: isEmpty(availType) ? null : availType,
            minSqft: isEmpty(minSqft) ? null : minSqft,
            maxSqft: isEmpty(maxSqft) ? null : maxSqft,
            minRent: isEmpty(minRent) ? null : minRent,
            maxRent: isEmpty(maxRent) ? null : maxRent,
            offset: page * pageSize,
            limit: pageSize,
        });
        setLoading(true);
        setLoadingAll(page === 0);
        apiFetch({path: urlPath})
            .then((result) => {
                let newSpaces = result['records'];
                let hasMore = result['has_more'];

                if (page === 0) {
                    setSpaces(newSpaces);
                } else {
                    setSpaces((spaces) => [...spaces, ...newSpaces]);
                }
                setHasMore(hasMore);
                setLoading(false);
                setLoadingAll(false);
            })
            .catch((error) => {
                setError(error);
                setLoading(false);
                setLoadingAll(false);
            });
    }

    useEffect(() => {
        setPage(0);
        fetch();
    }, [city, propertyType, subType, availType, minSqft, maxSqft, minRent, maxRent, pageSize]);

    useEffect(() => {
        fetch();
    }, [page]);

    if (error) {
        return <div>Error: {error.message}</div>
    } else {
        return <>
            <Stack spacing={2} sx={{alignContent: 'center', justifyContent: 'center'}}>
                <SpacesControls key='spaces-grid-controls'
                                city={city}
                                onCityChange={setCity}
                                propertyType={propertyType}
                                onTypeChange={setPropertyType}
                                subType={subType}
                                onSubTypeChange={setSubType}
                                availType={availType}
                                onAvailTypeChange={setAvailType}
                                minSqft={minSqft}
                                maxSqft={maxSqft}
                                onSqftChange={(range) => {
                                    setMinSqft(range[0]);
                                    setMaxSqft(range[1]);
                                }}
                                minRent={minRent}
                                maxRent={maxRent}
                                onRentChange={(range) => {
                                    setMinRent(range[0]);
                                    setMaxRent(range[1]);
                                }}
                                showControls={attributes.showControls}
                                onError={setError}/>
                {loadingAll && (
                    <Stack direction='row' sx={{alignContent: 'center', justifyContent: 'center'}}>
                        <CircularProgress/>
                    </Stack>
                )}
                {!loadingAll && (
                    <Box sx={{flexGrow: 1}}>
                        <Grid container spacing={2} sx={{minWidth: '320px'}}>
                            {spaces.map((space) => {
                                return (
                                    <Grid item key={space['Space_ID']}
                                          sm={12} md={6} lg={4} xl={3}>
                                        <SpaceCard space={space} dense/>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}
                {(loading && !loadingAll) && (
                    <Stack direction='row' sx={{alignContent: 'center', justifyContent: 'center'}}>
                        <CircularProgress/>
                    </Stack>
                )}
                {hasMore && (
                    <Stack direction='row' sx={{alignContent: 'center', justifyContent: 'center'}}>
                        <Button variant='contained' size='medium'
                                onClick={() => setPage(page + 1)}
                                sx={{width: '25%', minWidth: '128px'}}
                                disabled={loading}>
                            Load More
                        </Button>
                    </Stack>
                )}
            </Stack>
        </>;
    }
}

export default SpacesGrid;
